import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import Sidebar from "../components/layout/Sidebar";
import CallsPage from "../pages/CallsPage"; // adjust path
import MailPage from "./MailPage";
import ComposePage from "./ComposePage";
import SettingsPage from "./SettingsPage";
import NotesPage from "./NotesPage";
import ProfilePage from "./ProfilePage";

import ContactsPage from "./ContactsPage";
import AdminUsersPage from "./AdminUsersPage";
import ClientMasterPage from "./ClientMasterPage";
import LoginSliderManagePage from "./LoginSliderManagePage";
import MeetingPage from "./MeetingPage";
import DashboardPage from "./DashboardPage";
import useMailbox from "../hooks/useMailbox";
import useCompose from "../hooks/useCompose";
import useSignatures from "../hooks/useSignatures";
import useProfile from "../hooks/useProfile";
import useNotes from "../hooks/useNotes";
import useContacts from "../hooks/useContacts";
import { AUTH_STORAGE_KEY } from "../socket";
import Krivachat from "./Krivachat";
import "../styles.css";
import "../mobstyle.css";
import MobileBottomNav from "../components/layout/MobileBottomNav";
import { connectSocket, disconnectSocket } from "../socket";
import ComposeModal from "../components/compose/ComposeModalV2";
import LandingPage from "./LandingPage";

function KrivaMail({ auth,setVideoCall,setVoiceCall }) {
  const location = useLocation();
  const navigate = useNavigate();
  // Track if we already handled auto-compose
  const [autoComposeHandled, setAutoComposeHandled] = useState(false);
  // Track recipient for auto-compose
  const [autoComposeTo, setAutoComposeTo] = useState(null);

  const authHeaders = useMemo(() => {
    return auth?.token ? { Authorization: `Bearer ${auth.token}` } : null;
  }, [auth?.token]);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [toast, setToast] = useState("");
  const [showMobileList, setShowMobileList] = useState(true);
  const [selectedMailId, setSelectedMailId] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [starredState, setStarredState] = useState({});
  const [isMobile, setIsMobile] = useState(false);
  //const [showComposeModal, setShowComposeModal] = useState(false);
const [composeList, setComposeList] = useState([]);

  const mailbox = useMailbox(authHeaders);
  const signatureState = useSignatures(auth, authHeaders);
  const composeState = useCompose({
    authHeaders,
    signatureList: signatureState.signatureList,
    defaultSignatureId: signatureState.defaultSignatureId,
    signatureEditable: signatureState.editable,
    updateToast: setToast,
  });
  const profileState = useProfile(auth, authHeaders);
  const notesState = useNotes();
  const contactsState = useContacts();
  const role = Number(auth?.user?.role) || (auth?.user?.isAdmin ? 2 : 3);

  // const handleOpenComposeModal = useCallback(() => setShowComposeModal(true), []);
  // const handleCloseComposeModal = useCallback(() => setShowComposeModal(false), []);

const handleOpenComposeModal = useCallback((composeTo = null, options = {}) => {
  const shouldReset = options.reset ?? (composeTo == null);

  if (shouldReset) {
    try {
      composeState.resetComposeForm();
    } catch {}
  }

  setComposeList((prev) => [...prev, { id: Date.now(), composeTo }]);
}, [composeState]);
  // Auto-open compose modal if coming from service card
  useEffect(() => {
    if (!autoComposeHandled && location.state && location.state.composeTo) {
      setAutoComposeHandled(true);
      setAutoComposeTo(location.state.composeTo);
      handleOpenComposeModal(location.state.composeTo);
      // Remove state so it doesn't trigger again
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, autoComposeHandled, navigate]);

const handleCloseComposeModal = (id) => {
  setComposeList((prev) => prev.filter((c) => c.id !== id));
};
  const activeView = useMemo(() => {
    if (location.pathname.includes("/compose")) return "compose";
    if (location.pathname.includes("/settings")) return "settings";
    if (location.pathname.includes("/notes")) return "notes";
    if (location.pathname.includes("/contacts")) return "contacts";
    if (location.pathname.includes("/users")) return "users";
    if (location.pathname.includes("/starred")) return "starred";
    if (location.pathname.includes("/sent")) return "sent";
    if (location.pathname.includes("/archive")) return "archive";
    if (location.pathname.includes("/trash")) return "trash";
    if (location.pathname.includes("/drafts")) return "drafts";
    return "inbox";
  }, [location.pathname]);

  const isMailView = activeView === "inbox" || activeView === "sent" || activeView === "starred" || activeView === "drafts" || activeView === "trash" || activeView === "all" || activeView === "archive";

  useEffect(() => {
    if (isMailView) {
      setShowMobileList(true);
    }
  }, [location.pathname, isMailView]);
useEffect(() => {
  if (!toast) return;

  const timer = setTimeout(() => {
    setToast("");
  }, 3000);

  return () => clearTimeout(timer);
}, [toast]);
  // connect socket when authenticated; disconnect on logout
  useEffect(() => {
     if (auth?.token) {
    try {
      connectSocket(auth.token);
    } catch (e) {}
  }

  }, [auth?.token]);

  useEffect(() => {
    setStarredState((prev) => {
      let mutated = false;
      const next = { ...prev };
      const seedFromList = (list) => {
        list.forEach((mail) => {
          if (!mail || mail.id == null) return;
          const key = String(mail.id);
          if (Object.prototype.hasOwnProperty.call(next, key)) return;
          if (typeof mail.starred === "boolean") {
            next[key] = mail.starred;
            mutated = true;
          }
        });
      };
      seedFromList(mailbox.inbox);
      seedFromList(mailbox.sent);
      return mutated ? next : prev;
    });
  }, [mailbox.inbox, mailbox.sent]);

  const starredMails = useMemo(() => {
    const seen = new Map();
    const lists = [mailbox.inbox, mailbox.sent];
    lists.forEach((collection) => {
      collection.forEach((mail) => {
        if (!mail || mail.id == null) return;
        const key = String(mail.id);
        if (seen.has(key)) return;
        const isStarred = Object.prototype.hasOwnProperty.call(starredState, key)
          ? starredState[key]
          : Boolean(mail.starred);
        if (isStarred) {
          seen.set(key, mail);
        }
      });
    });
    return Array.from(seen.values());
  }, [mailbox.inbox, mailbox.sent, starredState]);

  const mailList = useMemo(() => {
    if (activeView === "sent") return mailbox.sent;
    if (activeView === "starred") return starredMails;
    if (activeView === "drafts") return mailbox.drafts || [];
    if (activeView === "trash") return mailbox.trash || [];
    if (activeView === "archive") return mailbox.archive || [];
    if (activeView === "all") {
      const seen = new Map();
      [...(mailbox.inbox || []), ...(mailbox.sent || []), ...(mailbox.drafts || [])].forEach(m => {
        if (m?.id != null) seen.set(String(m.id), m);
      });
      return Array.from(seen.values());
    }
    if (activeView === "inbox") return mailbox.inbox;
    return [];
  }, [activeView, mailbox.inbox, mailbox.sent, mailbox.drafts, mailbox.trash, mailbox.archive, starredMails]);
  const filteredMails = useMemo(() => {
    if (!["inbox", "sent", "starred", "drafts", "trash", "archive","all"].includes(activeView)) return mailList;
    if (!searchQuery) return mailList;
    if (!mailList.length) return mailList;
    const needle = searchQuery.toLowerCase();
    return mailList.filter((mail) =>
      [mail.subject, mail.from, mail.to, mail.bodyPlain]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle))
    );
  }, [activeView, mailList, searchQuery]);

  const selectedMail = useMemo(() => {
    if (!["inbox", "sent", "starred", "drafts", "trash", "archive", "all"].includes(activeView)) return null;
    return mailList.find((mail) => mail.id === selectedMailId) || null;
  }, [activeView, mailList, selectedMailId]);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 640);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setShowMobileList(true);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!["inbox", "sent", "starred", "drafts", "archive", "all"].includes(activeView)) {
      setSelectedMailId(null);
      return;
    }
    if (!mailList.length) {
      setSelectedMailId(null);
      return;
    }
    setSelectedMailId((prev) => {
      if (prev && mailList.some((mail) => mail.id === prev)) {
        return prev;
      }
      return mailList[0].id;
    });
  }, [activeView, mailList]);

  useEffect(() => {
    if (activeView === "compose" || activeView === "settings") {
      setShowMobileList(true);
    }
  }, [activeView]);

  useEffect(() => {
    if (selectedMail?.unread && activeView === "inbox") {
      mailbox.markAsRead(selectedMail.id);
    }
  }, [activeView, mailbox, selectedMail]);

  const handleToggleStar = useCallback((mail, nextValue) => {
    if (!mail || mail.id == null) return;
    const key = String(mail.id);
    setStarredState((prev) => ({ ...prev, [key]: Boolean(nextValue) }));
  }, []);
  
  const toggleSelect = useCallback((mailId, checked) => {
    setSelectedIds((prev) => {
      const exists = prev.includes(mailId);
      if (checked) {
        if (exists) return prev;
        return [mailId, ...prev];
      }
      if (!exists) return prev;
      return prev.filter((id) => id !== mailId);
    });
  }, []);

  const toggleSelectAll = useCallback((checked, mails) => {
    if (checked) {
      const ids = Array.isArray(mails) ? mails.map((m) => m.id) : [];
      setSelectedIds(ids);
    } else {
      setSelectedIds([]);
    }
  }, []);

  const markSelectedRead = useCallback(async () => {
    if (!selectedIds.length) return;
    try {
      await Promise.all(selectedIds.map((id) => mailbox.markAsRead(id)));
    } catch {}
    setSelectedIds([]);
  }, [selectedIds, mailbox]);

  const markSelectedUnread = useCallback(async () => {
    if (!selectedIds.length) return;
    selectedIds.forEach((id) => mailbox.markAsUnread(id));
    setSelectedIds([]);
  }, [selectedIds, mailbox]);

  const starSelected = useCallback(() => {
    if (!selectedIds.length) return;
    setStarredState((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[String(id)] = true;
      });
      return next;
    });
    setSelectedIds([]);
  }, [selectedIds]);

  const unstarSelected = useCallback(() => {
    if (!selectedIds.length) return;
    setStarredState((prev) => {
      const next = { ...prev };
      selectedIds.forEach((id) => {
        next[String(id)] = false;
      });
      return next;
    });
    setSelectedIds([]);
  }, [selectedIds]);

  const moveSelected = useCallback(() => {
    if (!selectedIds.length) return;
    const dest = window.prompt("Move to folder (inbox/sent):", "inbox");
    if (!dest) return;
    if (!["inbox", "sent", "draft"].includes(dest)) {
      setToast("Invalid folder");
      return;
    }
    mailbox.moveMails(selectedIds, dest);
    setSelectedIds([]);
  }, [selectedIds, mailbox]);

  const applyLabelSelected = useCallback((labelId) => {
    if (!selectedIds.length || !labelId) return;
    mailbox.applyLabelToMails(selectedIds, labelId);
    setSelectedIds([]);
    setToast("Label applied to selected messages.");
  }, [selectedIds, mailbox]);

  const removeLabelSelected = useCallback((labelId) => {
    if (!selectedIds.length || !labelId) return;
    mailbox.removeLabelFromMails(selectedIds, labelId);
    setSelectedIds([]);
    setToast("Label removed from selected messages.");
  }, [selectedIds, mailbox]);

  const deleteSelected = useCallback(() => {
    if (!selectedIds.length) return;
    if (!window.confirm("Delete selected messages?")) return;
    if (activeView === "trash") {
      // permanently delete
      mailbox.removeMails(selectedIds);
    } else {
      // move to trash
      mailbox.moveMails(selectedIds, "trash");
    }
    setSelectedIds([]);
  }, [selectedIds, mailbox]);

  const handleDeleteMail = useCallback((mailId) => {
    if (!mailId) return;
    if (!window.confirm("Delete this message?")) return;
    if (activeView === "trash") {
      mailbox.removeMails([mailId]);
    } else {
      mailbox.moveMails([mailId], "trash");
    }
    setSelectedMailId(null);
  }, [mailbox]);

  const archiveSelected = useCallback(() => {
    if (!selectedIds.length) return;
    // Move selected mails to server-side archive folder
    try {
      mailbox.moveMails(selectedIds, "archive");
      setToast("Archived selected messages.");
      setSelectedIds([]);
    } catch (e) {
      console.error("Failed to archive selected messages", e);
    }
  }, [selectedIds, mailbox]);

  const reportSpamSelected = useCallback(() => {
    if (!selectedIds.length) return;
    mailbox.removeMails(selectedIds);
    setToast("Reported selected messages as spam.");
    setSelectedIds([]);
  }, [selectedIds, mailbox]);

  const starredCount = starredMails.length;
  const noteCount = notesState.notes.length;
  const contactsCount = contactsState.contacts.length;
  const draftsCount = mailbox.drafts ? mailbox.drafts.length : 0;
  const trashCount = mailbox.trash ? mailbox.trash.length : 0;

  const archiveCount = useMemo(() => (Array.isArray(mailbox.archive) ? mailbox.archive.length : 0), [mailbox.archive]);

  const recentEmails = useMemo(() => {
    const all = [...(mailbox.inbox || []), ...(mailbox.sent || []), ...(mailbox.drafts || []), ...(mailbox.trash || [])];
    const set = new Set();
    all.forEach((m) => {
      if (!m) return;
      const from = (m.from || "").toString().trim();
      const to = (m.to || "").toString().trim();
      if (from) set.add(from);
      if (to) {
        to.split(/[,;\s]+/).forEach(t => { if (t) set.add(t); });
      }
    });
    return Array.from(set).slice(0, 200);
  }, [mailbox.inbox, mailbox.sent, mailbox.drafts, mailbox.trash]);

  const handleSelectMail = useCallback(
    (mail) => {
      if (!mail) return;
      // If clicking a draft, open compose with draft contents
      if (activeView === "drafts") {
        try {
          composeState.handleEditDraft?.(mail);
        } catch {}
        handleOpenComposeModal();
        return;
      }

      setSelectedMailId(mail.id);
      if (activeView === "inbox") {
        mailbox.markAsRead(mail.id);
      }
      if (isMobile) {
        setShowMobileList(false);
      }
    },
    [activeView, isMobile, mailbox, composeState, navigate]
  );

  const handleComposeNavigate = useCallback(() => {
    handleOpenComposeModal();
  }, [handleOpenComposeModal]);

  const handleReply = useCallback((mail) => {
    const target = (mail && mail.id != null) ? mail : selectedMail;
    if (!target) return;
    const result = composeState.handleReply?.(target);
    if (result) {
      handleOpenComposeModal(null, { reset: false });
    }
  }, [composeState, handleOpenComposeModal, selectedMail]);

  const handleForward = useCallback((mail) => {
    const target = (mail && mail.id != null) ? mail : selectedMail;
    if (!target) return;
    const result = composeState.handleForward?.(target);
    if (result) {
      handleOpenComposeModal(null, { reset: false });
    }
  }, [composeState, handleOpenComposeModal, selectedMail]);

  const handleReplyAll = useCallback((mail) => {
    const target = (mail && mail.id != null) ? mail : selectedMail;
    if (!target) return;
    const result = composeState.handleReplyAll?.(target);
    if (result) {
      handleOpenComposeModal(null, { reset: false });
    }
  }, [composeState, handleOpenComposeModal, selectedMail]);

  const handleMailSent = useCallback(
    (newMail) => {
      if (newMail) {
        mailbox.appendSentMail(newMail);
        setSelectedMailId(newMail.id);
        // If this was a reply that changed the original mail status, update local mailbox immediately
        try {
          if (newMail.replyToId && newMail.mailStatus) {
            mailbox.updateMail({ id: newMail.replyToId, mailStatus: newMail.mailStatus });
          }
        } catch (e) {}
      } else {
        mailbox.loadFolder("sent");
      }
    },
    [mailbox]
  );

  function LabelViewWrapper() {
    const { labelId } = useParams();
    const folderTitle = useMemo(() => `Label: ${labelId}`, [labelId]);
    const mailsForLabel = useMemo(() => {
      const all = [...(mailbox.inbox || []), ...(mailbox.sent || []), ...(mailbox.drafts || [])];
      const seen = new Map();
      all.forEach((m) => {
        if (!m || m.id == null) return;
        seen.set(String(m.id), m);
      });
      const list = Array.from(seen.values()).filter((m) => Array.isArray(m.labels) && m.labels.includes(labelId));
      return list;
    }, [mailbox.inbox, mailbox.sent, mailbox.drafts, labelId]);

    return (
      <MailPage
        folder={`label-${labelId}`}
        title={folderTitle}
        mails={mailsForLabel}
        selectedMailId={selectedMailId}
        onSelectMail={handleSelectMail}
        selectedMail={selectedMail}
        isMobile={isMobile}
        showMobileList={showMobileList}
        onToggleMobileList={setShowMobileList}
        loading={mailbox.loading}
        error={mailbox.error}
        starredMap={starredState}
        onToggleStar={handleToggleStar}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onMarkReadSelected={markSelectedRead}
        onMarkUnreadSelected={markSelectedUnread}
        onStarSelected={starSelected}
        onUnstarSelected={unstarSelected}
        onMoveSelected={moveSelected}
        onApplyLabelSelected={applyLabelSelected}
        onRemoveLabelSelected={removeLabelSelected}
        onDeleteSelected={deleteSelected}
        onArchiveSelected={archiveSelected}
        onReportSpamSelected={reportSpamSelected}
        onDeleteMail={handleDeleteMail}
        onOpenComposeModal={handleOpenComposeModal}
        externalBlocked={mailbox.externalBlocked}
      />
    );
  }

  function AllViewWrapper() {
    const mailsForAll = useMemo(() => {
      const all = [...(mailbox.inbox || []), ...(mailbox.sent || []), ...(mailbox.drafts || [])];
      const seen = new Map();
      all.forEach((m) => {
        if (!m || m.id == null) return;
        seen.set(String(m.id), m);
      });
      return Array.from(seen.values());
    }, [mailbox.inbox, mailbox.sent, mailbox.drafts]);

    return (
      <MailPage
        folder="all"
        mails={mailsForAll}
        selectedMailId={selectedMailId}
        onSelectMail={handleSelectMail}
        selectedMail={selectedMail}
        isMobile={isMobile}
        showMobileList={showMobileList}
        onToggleMobileList={setShowMobileList}
        loading={mailbox.loading}
        error={mailbox.error}
        starredMap={starredState}
        onToggleStar={handleToggleStar}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onMarkReadSelected={markSelectedRead}
        onMarkUnreadSelected={markSelectedUnread}
        onStarSelected={starSelected}
        onUnstarSelected={unstarSelected}
        onMoveSelected={moveSelected}
        onApplyLabelSelected={applyLabelSelected}
        onRemoveLabelSelected={removeLabelSelected}
        onDeleteSelected={deleteSelected}
        onArchiveSelected={archiveSelected}
        onReportSpamSelected={reportSpamSelected}
        onDeleteMail={handleDeleteMail}
        onOpenComposeModal={handleOpenComposeModal}
        externalBlocked={mailbox.externalBlocked}
      />
    );
  }

  function StatusViewWrapper() {
    const { status } = useParams();
    const mailsForStatus = useMemo(() => {
      const all = [...(mailbox.inbox || []), ...(mailbox.sent || []), ...(mailbox.drafts || [])];
      const seen = new Map();
      all.forEach((m) => {
        if (!m || m.id == null) return;
        seen.set(String(m.id), m);
      });
      return Array.from(seen.values()).filter((m) => String((m.mailStatus || "")).toLowerCase() === String((status || "")).toLowerCase());
    }, [mailbox.inbox, mailbox.sent, mailbox.drafts, status]);

    return (
      <MailPage
        folder={`status-${status}`}
        mails={mailsForStatus}
        selectedMailId={selectedMailId}
        onSelectMail={handleSelectMail}
        selectedMail={selectedMail}
        isMobile={isMobile}
        showMobileList={showMobileList}
        onToggleMobileList={setShowMobileList}
        loading={mailbox.loading}
        error={mailbox.error}
        starredMap={starredState}
        onToggleStar={handleToggleStar}
        onReply={handleReply}
        onReplyAll={handleReplyAll}
        onForward={handleForward}
        selectedIds={selectedIds}
        onToggleSelect={toggleSelect}
        onToggleSelectAll={toggleSelectAll}
        onMarkReadSelected={markSelectedRead}
        onMarkUnreadSelected={markSelectedUnread}
        onStarSelected={starSelected}
        onUnstarSelected={unstarSelected}
        onMoveSelected={moveSelected}
        onApplyLabelSelected={applyLabelSelected}
        onRemoveLabelSelected={removeLabelSelected}
        onDeleteSelected={deleteSelected}
        onArchiveSelected={archiveSelected}
        onReportSpamSelected={reportSpamSelected}
        onDeleteMail={handleDeleteMail}
        onOpenComposeModal={handleOpenComposeModal}
      />
    );
  }

  const handleNavigateSent = useCallback(
    (newMail) => {
      navigate("/kmail/sent");
      if (newMail) {
        setSelectedMailId(newMail.id);
      }
    },
    [navigate]
  );

  const handleSignOut = useCallback(() => {
    try {
      localStorage.removeItem(AUTH_STORAGE_KEY);
    } catch {}
    navigate("/login");
  }, [navigate]);

  const userEmail = auth?.user?.email || auth?.user?.username || "";
  const userFullName = profileState.profileDetails?.fullName || auth?.user?.fullName || "";
  const profileImageUrl = profileState.profileImagePreview || "";
  const userInitial = (userFullName?.[0] || userEmail?.[0] || "U").toUpperCase();

  const sidebarProps = {
    collapsed: sidebarCollapsed,
    onToggleCollapse: () => setSidebarCollapsed((prev) => !prev),
    unreadCount: mailbox.unreadCount,
    onMoveSelected: moveSelected,
    onApplyLabelSelected: applyLabelSelected,
    onRemoveLabelSelected: removeLabelSelected,
    onDeleteSelected: deleteSelected,
    onDeleteMail: handleDeleteMail,
    onArchiveSelected: archiveSelected,
    onReportSpamSelected: reportSpamSelected,
    inboxCount: mailbox.inbox.length,
    sentCount: mailbox.sent.length,
    archiveCount: archiveCount,
    trashCount: trashCount,
    starredCount: starredCount,
    draftsCount: draftsCount,
    noteCount: noteCount,
    contactsCount: contactsCount,
    recentEmails: recentEmails,
    isMobile: isMobile,
    userFullName: userFullName,
    role: role,
    
  };

  return (
    <div className="mail-shell">
      {!isMobile && <Sidebar {...sidebarProps} />}

      {/* <ComposeModal
        // open={showComposeModal}
        onClose={handleCloseComposeModal}
        compose={composeState}
        signatureList={signatureState.signatureList}
        defaultSignatureId={signatureState.defaultSignatureId}
        authHeaders={authHeaders}
        onMailSent={() => { setShowComposeModal(false); }}
        setToast={setToast}
        isMobile={isMobile}
      /> */}
      {composeList.map((item, index) => (
        <ComposeModal
          key={item.id}
          open={true}
          onClose={() => handleCloseComposeModal(item.id)}
          style={{
            right: 20 + index * 320,
            bottom: 20
          }}
          compose={composeState}
          signatureList={signatureState.signatureList}
          defaultSignatureId={signatureState.defaultSignatureId}
          authHeaders={authHeaders}
          composeTo={item.composeTo}
          onMailSent={() => {
            setToast("Mail saved successfully ✅");
            handleCloseComposeModal(item.id);
          }}
          setToast={setToast}
          isMobile={isMobile}
        />
      ))}

      <main className="mail-main">
        
        {/* <Header
          onSearch={(value) => setSearchQuery(value.toLowerCase())}
          onCompose={handleComposeNavigate}
          onReply={handleReply}
          onForward={handleForward}
          selectedMail={selectedMail}
          toast={toast}
          isMobile={isMobile}
          userEmail={userEmail}
          userFullName={userFullName}
          profileImageUrl={profileImageUrl}
          userInitial={userInitial}
          onOpenSettings={() => navigate("/kmail/settings")}
          onSignOut={handleSignOut}
          onOpenNotes={() => navigate("/kmail/notes")}
          showComposeFab={!(activeView === "compose" || (isMobile && !showMobileList))}
          sidebarProps={sidebarProps}
        /> */}

        <Routes>
          <Route
            path="/"
            element={
              <MailPage
                folder="inbox"
                mails={filteredMails}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                selectedMail={selectedMail}
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                isMobile={isMobile}
                showMobileList={showMobileList}
                onToggleMobileList={setShowMobileList}
                loading={mailbox.loading}
                error={mailbox.error}
                starredMap={starredState}
                onToggleStar={handleToggleStar}
                onOpenComposeModal={handleOpenComposeModal}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onMarkReadSelected={markSelectedRead}
                onMarkUnreadSelected={markSelectedUnread}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onMoveSelected={moveSelected}
                onApplyLabelSelected={applyLabelSelected}
                onRemoveLabelSelected={removeLabelSelected}
                onDeleteSelected={deleteSelected}
                onArchiveSelected={archiveSelected}
                onReportSpamSelected={reportSpamSelected}
                onDeleteMail={handleDeleteMail}
                externalBlocked={mailbox.externalBlocked}
              />
            }
          />
          <Route
            path="/sent"
            element={
              <MailPage
                folder="sent"
                mails={filteredMails}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                selectedMail={selectedMail}
                isMobile={isMobile}
                showMobileList={showMobileList}
                onToggleMobileList={setShowMobileList}
                loading={mailbox.loading}
                error={mailbox.error}
                starredMap={starredState}
                onToggleStar={handleToggleStar}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onMarkReadSelected={markSelectedRead}
                onMarkUnreadSelected={markSelectedUnread}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onMoveSelected={moveSelected}
                onApplyLabelSelected={applyLabelSelected}
                onRemoveLabelSelected={removeLabelSelected}
                onDeleteSelected={deleteSelected}
                onArchiveSelected={archiveSelected}
                onReportSpamSelected={reportSpamSelected}
                onDeleteMail={handleDeleteMail}
                onOpenComposeModal={handleOpenComposeModal}
                externalBlocked={mailbox.externalBlocked}
              />
            }
          />
         <Route path="/" element={<AllViewWrapper />} />

          <Route path="/status/:status" element={<StatusViewWrapper />} />
          <Route
            path="/drafts"
            element={
              <MailPage
                folder="drafts"
                mails={filteredMails}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                selectedMail={selectedMail}
                isMobile={isMobile}
                showMobileList={showMobileList}
                onToggleMobileList={setShowMobileList}
                loading={mailbox.loading}
                error={mailbox.error}
                starredMap={starredState}
                onToggleStar={handleToggleStar}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onMarkReadSelected={markSelectedRead}
                onMarkUnreadSelected={markSelectedUnread}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onMoveSelected={moveSelected}
                onApplyLabelSelected={applyLabelSelected}
                onRemoveLabelSelected={removeLabelSelected}
                onDeleteSelected={deleteSelected}
                onArchiveSelected={archiveSelected}
                onReportSpamSelected={reportSpamSelected}
                onDeleteMail={handleDeleteMail}
                onOpenComposeModal={handleOpenComposeModal}
                externalBlocked={mailbox.externalBlocked}
              />
            }
          />
          <Route
            path="/starred"
            element={
              <MailPage
                folder="starred"
                mails={filteredMails}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                selectedMail={selectedMail}
                isMobile={isMobile}
                showMobileList={showMobileList}
                onToggleMobileList={setShowMobileList}
                loading={mailbox.loading}
                error={mailbox.error}
                starredMap={starredState}
                onToggleStar={handleToggleStar}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onMarkReadSelected={markSelectedRead}
                onMarkUnreadSelected={markSelectedUnread}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onDeleteMail={handleDeleteMail}
                onOpenComposeModal={handleOpenComposeModal}
                externalBlocked={mailbox.externalBlocked}
              />
            }
          />
          <Route
            path="/archive"
            element={
              <MailPage
                folder="archive"
                mails={filteredMails}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                selectedMail={selectedMail}
                isMobile={isMobile}
                showMobileList={showMobileList}
                onToggleMobileList={setShowMobileList}
                loading={mailbox.loading}
                error={mailbox.error}
                starredMap={starredState}
                onToggleStar={handleToggleStar}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onMarkReadSelected={markSelectedRead}
                onMarkUnreadSelected={markSelectedUnread}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onMoveSelected={moveSelected}
                onApplyLabelSelected={applyLabelSelected}
                onRemoveLabelSelected={removeLabelSelected}
                onDeleteSelected={deleteSelected}
                onArchiveSelected={archiveSelected}
                onReportSpamSelected={reportSpamSelected}
                onDeleteMail={handleDeleteMail}
                onOpenComposeModal={handleOpenComposeModal}
                externalBlocked={mailbox.externalBlocked}
              />
            }
          />
          <Route
            path="/trash"
            element={
              <MailPage
                folder="trash"
                mails={filteredMails}
                selectedMailId={selectedMailId}
                onSelectMail={handleSelectMail}
                selectedMail={selectedMail}
                isMobile={isMobile}
                showMobileList={showMobileList}
                onToggleMobileList={setShowMobileList}
                loading={mailbox.loading}
                error={mailbox.error}
                starredMap={starredState}
                onToggleStar={handleToggleStar}
                onReply={handleReply}
                onReplyAll={handleReplyAll}
                onForward={handleForward}
                selectedIds={selectedIds}
                onToggleSelect={toggleSelect}
                onToggleSelectAll={toggleSelectAll}
                onMarkReadSelected={markSelectedRead}
                onMarkUnreadSelected={markSelectedUnread}
                onStarSelected={starSelected}
                onUnstarSelected={unstarSelected}
                onMoveSelected={moveSelected}
                onApplyLabelSelected={applyLabelSelected}
                onRemoveLabelSelected={removeLabelSelected}
                onDeleteSelected={deleteSelected}
                onArchiveSelected={archiveSelected}
                onReportSpamSelected={reportSpamSelected}
                onDeleteMail={handleDeleteMail}
                onOpenComposeModal={handleOpenComposeModal}
              />
            }
          />
          <Route
            path="/compose"
            element={
              <ComposePage
                compose={composeState}
                signatureList={signatureState.signatureList}
                defaultSignatureId={signatureState.defaultSignatureId}
                authHeaders={authHeaders}
                onMailSent={handleMailSent}
                onNavigateSent={handleNavigateSent}
                setToast={setToast}
                onDraftSaved={(mail) => {
                  // reload drafts when a draft is saved
                  try {
                    mailbox.loadFolder("drafts");
                  } catch {}
                }}
                isMobile={isMobile}
              />
            }
          />
          <Route path="/dashboard" element={<DashboardPage onReply={handleReply} onReplyAll={handleReplyAll} onForward={handleForward} onDeleteMail={handleDeleteMail} onOpenComposeModal={handleOpenComposeModal} />} />
          <Route path="/settings" element={<SettingsPage signatureState={signatureState} profileState={profileState} onSignOut={handleSignOut} />} />
          <Route
            path="/users"
            element={
              (role === 1 || role === 2) ? (
                <AdminUsersPage authHeaders={authHeaders} setToast={setToast} />
              ) : (
                <Navigate to="/Kmail" replace />
              )
            }
          />
          <Route
            path="/clients"
            element={
              (role === 1 || role === 2) ? (
                <ClientMasterPage authHeaders={authHeaders} setToast={setToast} />
              ) : (
                <Navigate to="/Kmail" replace />
              )
            }
          />
       <Route
  path="calls"
  element={
    <CallsPage
      setVideoCall={setVideoCall}
      setVoiceCall={setVoiceCall}
       onOpenComposeModal={handleOpenComposeModal}
    />
  }
/>
          <Route
            path="/slider"
            element={
              (role === 1 || role === 2) ? (
                <LoginSliderManagePage authHeaders={authHeaders} setToast={setToast} />
              ) : (
                <Navigate to="/Kmail" replace />
              )
            }
          />
          <Route
            path="/contacts"
            element={
              <ContactsPage
                contacts={contactsState.contacts}
                addContact={contactsState.addContact}
                updateContact={contactsState.updateContact}
                deleteContact={contactsState.deleteContact}
                setVideoCall={setVideoCall}
                setVoiceCall={setVoiceCall}
                onOpenComposeModal={handleOpenComposeModal}
              />
            }
          />
          <Route path="profile" element={<ProfilePage />} />

          <Route
            path="/label/:labelId"
            element={<LabelViewWrapper />}
          />
          <Route
            path="/notes"
            element={
              <NotesPage
                notes={notesState.notes}
                addNote={notesState.addNote}
                updateNote={notesState.updateNote}
                deleteNote={notesState.deleteNote}
              />
            }
          />
       
       
         <Route
  path="krivachat"
  element={
        <Krivachat
      setVideoCall={setVideoCall}
      setVoiceCall={setVoiceCall}
      onOpenComposeModal={handleOpenComposeModal}
    />
  }
/>
          <Route path="/meeting" element={<MeetingPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="*" element={<Navigate to="/Kmail" replace />} />
        </Routes>
      </main>
   
    {isMobile && <MobileBottomNav onInboxClick={() => setShowMobileList(true)} trashCount={mailbox.trash ? mailbox.trash.length : 0} archiveCount={archiveCount} onOpenSettings={() => navigate("/kmail/settings")} onSignOut={handleSignOut} />}
   {toast && (
  <div className="toast-message">
    {toast}
  </div>
)}
    </div>
    
  );
  
}

export default KrivaMail;
